/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { LoadingScreen } from 'components/LoadingScreen';
import { useStore } from 'context/store-context';
import { withNotification } from 'components/Notification';

import { Tabs } from 'components/Tabs';
import CustomizeSettings from './CustomizeSettings';
import Preview from './Preview';
import CodeScript from './CodeScript';
import { generateApiKey, NotificationTypes, apiErrorHandler } from 'utils';
import { useMutation } from '@apollo/client';
import { UPDATE_STORE } from 'gql/mutations';

const tabs = [
  { name: 'Customize Portal', description: 'Step 1: Design your portal' },
  { name: 'Preview', description: 'Step 2: Preview your design' },
  { name: 'Generate Code/Script', description: 'Step 3: Get your code' },
];

export const BasicSetupForm: IForm = {
  key: '',
  template: '1',
  logo: {
    url: 'http://res.cloudinary.com/media-resource/image/upload/v1738345674/shipaid-dev/tfyiyogbc53wp0dllwfv.png',
    size: '250',
    position: 'start',
    enableLogo: true,
  },
  theme: {
    primary: '#2563EB',
    border: '#2563EB',
    background: '#FFFFFF',
    text: '#000000',
    buttonText: '#FFFFFF',
  },
  backgrounds: {
    homePageBanner:
      'http://res.cloudinary.com/media-resource/image/upload/v1738345872/shipaid-dev/mov3594qtj0swrphckzw.jpg',
    enableBanner: true,
    homePageBackgroundColor: '',
    formOverlayOpacity: '1',
  },
  labels: {
    homePageHeader: 'Lost or Damaged Package?',
    homePageDescription:
      "You ordered from your favorite brand online but your package didn't arrive as you had hoped. No worries, things happen! Let's find your order and report it to the brand you purchased from so they can resolve your issue in no time.",
    emailPlaceholder: 'Email or Phone',
    orderNumberPlaceholder: 'Order Number',
    searchButtonText: 'Search',
    newDeliveryIssueTitleLabel: 'New Delivery Issue',
    newDeliveryIssueDescLabel:
      'Please fill out the information below to file an issue. Provide as much detail to assist with quick resolution',
    itemsHeadingLabel: 'Items',
    selectIssueItemsLabel: 'Select issue item(s)',
    selectAtleastOneItemErrorLabel: 'Please select one item',
    causeHeadingLabel: 'Cause',
    selectCauseDescLabel:
      'Choose from one of the causes below and input additional information',
    selectIssueCauseLabel: 'Please select the cause for the issue',
    uploadImagesHeadingLabel: 'Upload Images',
    uploadImagesDescLabel: 'Add images that illustrate the issue',
    uploadImagesMaxLabel:
      'Add up to 5 images that illustrate the issue and for future reference',
    uploadProgressLabel: 'Please wait, upload is in progress',
    uploadAtleastOneImageLabel: 'Please upload at least one image',
    uploadVideosHeadingLabel: 'Upload Videos',
    uploadVideosDescLabel: 'Add videos that illustrate the issue',
    uploadVideosMaxLabel:
      'Add up to 1 videos that illustrate the issue and for future reference',
    uploadAtleastOneVideoLabel: 'Please upload at least one video',
    preferredSolutionHeadingLabel: 'Preferred Resolution',
    preferredSolutionDescLabel:
      'Choose how you prefer for us to resolve this issue',
    preferredSolutionOtherLabel:
      'Because you selected "Other" as the Preferred Resolution, please provide more details',
    preferredSolutionOtherTextAreaLabel: 'Provide Preferred Resolution details',
    notesHeadingLabel: 'Your Notes',
    notesDescLabel: 'Provide any notes that would help support',
    orderNumberLabel: 'Order Number',
    orderDateLabel: 'Order Date',
    orderTotalLabel: 'Order Total',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    shippedToLabel: 'Shipped To',
    trackingLabel: 'Tracking',
    estDeliveryDateLabel: 'Estimated Delivery Date',
    shippingAddressLabel: 'Shipping Address',
    refundPolicyLabel: 'Refund Policy',
    reviewIssueButtonLabel: 'Review Issue',
    downloadLabel: 'Download',
    issueFiledHeadingLabel: 'Issue Filed',
    claimInfoLabel:
      'Thank you for filing a delivery issue! Your issue number is',
    claimInfoDescLabel: 'You will hear back from',
    claimInfoContentLabel:
      'within the next 2-3 business days with a resolution and/or response.',
    itemsLabel: 'Items',
    summaryLabel: 'Summary',
    issueNotesLabel: 'Issue Notes',
    issueTotalLabel: 'Issue Total',
    issueImagesLabel: 'Issue Images',
    noImagesTextLabel: 'No Images added to this issue',
    videosLabel: 'Videos',
    noVideosTextLabel: 'No Videos added to this issue',
    contactLabel: 'Contact',
    contactDeclarationLabel:
      'If you have any questions or need to send more information about this issue, please write your message below. The information for this issue will be enclosed automatically for reference with your message. You will receive an email with further correspondance.',
    messageSentLabel: 'Message was sent!',
    sendMessageBtnLabel: 'Send Message',
    changeShippingAddressLabel: 'Change Shipping Address',
    addressLabel: 'Address',
    addressPlaceholderLabel: 'Address',
    address2Label: 'Address 2',
    address2PlaceholderLabel: 'Address 2',
    cityLabel: 'City',
    cityPlaceholderLabel: 'City',
    stateLabel: 'State',
    statePlaceholderLabel: 'State',
    zipLabel: 'Zip',
    zipPlaceholderLabel: 'Zip',
    countryLabel: 'Country',
    countryPlaceholderLabel: 'Country',
    changeAddressButtonLabel: 'Change Address',
    reviewIssueHeadingLabel: 'Review Issue',
    reviewIssueDescLabel:
      'Please review the issue below and ensure all the information is correct before submitting',
    imagesHeadingLabel: 'Images',
    editIssueLabel: 'Edit Issue',
    submitIssueButtonLabel: 'Submit Issue',
  },
  containerStyle: {
    borderRadius: '10',
  },
};

export interface IForm {
  key: string;
  template?: '1' | '2' | '3';
  logo?: {
    url?: string;
    size?: string;
    position?: 'start' | 'center' | 'end';
    enableLogo?: boolean;
  };
  theme?: {
    primary?: string;
    border?: string;
    background?: string;
    text?: string;
    buttonText?: string;
  };
  backgrounds?: {
    homePageBanner?: string;
    enableBanner?: boolean;
    homePageBackgroundColor?: string;
    formOverlayOpacity?: string;
  };
  labels?: {
    [key: string]: string;
  };
  containerStyle?: {
    borderRadius?: string;
  };
}

export type KeyType = keyof IForm;

export const EmbedClaimPortal = withNotification(
  ({ showNotification }: any) => {
    const { storeId, storeLoading, storeProperties } = useStore();

    const [updateStoreField, { loading: onSaveLoading }] = useMutation(
      UPDATE_STORE,
      {},
    );

    const [tab, setTab] = useState('Customize Portal');

    const [form, setForm] = useState<IForm>({
      ...BasicSetupForm,
    });

    useEffect(() => {
      if (storeProperties && storeProperties.embedScriptSettings) {
        setForm((form) => ({
          ...form,
          key: storeProperties?.embedScriptSettings?.key || generateApiKey(),
          ...(storeProperties.embedScriptSettings || {}),
        }));
      }
    }, [storeProperties]);

    const setFormValues = (
      key: KeyType,
      value: string | Record<string, string | boolean | number>,
    ) => {
      setForm((form) => {
        const currentVal = form[key];

        const updates = {
          ...form,
          [key]:
            typeof value === 'string'
              ? value
              : {
                  ...(typeof currentVal === 'object' && currentVal
                    ? currentVal
                    : {}),
                  ...value,
                },
        };

        updateStoreField({
          variables: {
            storeId: storeId,
            set: {
              embedScriptSettings: {
                ...updates,
                key: updates.key || generateApiKey(),
              },
            },
          },
        });

        return updates;
      });
    };

    const onSaveSettings = () => {
      let key = form.key;

      if (!key) {
        key = generateApiKey();
        setForm({ key });
      }

      updateStoreField({
        variables: {
          storeId: storeId,
          set: {
            embedScriptSettings: {
              ...form,
              key,
            },
          },
        },
      })
        .then(() => {
          showNotification(
            NotificationTypes.success,
            'Portal settings saved successfully',
          );
        })
        .catch((error) => {
          const newError = apiErrorHandler(error);
          showNotification(
            NotificationTypes.error,
            newError?.message || 'Failed to save portal settings',
          );
        });
    };

    const onReset = () => {
      if (
        window.confirm(
          'Are you sure, this action will return to default settings',
        )
      ) {
        const defaultSettings = {
          ...BasicSetupForm,
          key: form.key || generateApiKey(),
        };
        setForm({ ...defaultSettings });

        updateStoreField({
          variables: {
            storeId: storeId,
            set: {
              embedScriptSettings: defaultSettings,
            },
          },
        });

        showNotification(
          NotificationTypes.success,
          'All customizations are reset',
        );
      }
    };

    useEffect(() => {
      if (!document.getElementById('shipaid-embed-claim-portal-script')) {
        const scriptTag = document.createElement('script');
        scriptTag.id = 'shipaid-embed-claim-portal';
        scriptTag.src = process.env
          .REACT_APP_CLAIM_PORTAL_EMBEDSCRIPT_URL as string;

        document.head.appendChild(scriptTag);
      }
    }, []);

    if (storeLoading && !storeId && !storeProperties) return <LoadingScreen />;
    return (
      <div className="w-full">
        <div className="border-b border-[#e1e3e5] mb-6">
          <Tabs
            tabs={tabs}
            selectedTab={tab}
            setSelectedTab={(t: string) => setTab(t)}
          />
        </div>

        <div>
          {tabs[0].name === tab && (
            <CustomizeSettings
              form={form}
              setForm={setFormValues}
              onSaveSettings={onSaveSettings}
              onSaveLoading={onSaveLoading}
              onReset={onReset}
            />
          )}
          {tabs[1].name === tab && (
            <div className="w-full pb-6">
              <div className="mb-6 pb-5 border-b border-[#e1e3e5]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs font-semibold">
                    2
                  </span>
                  <h1 className="text-[28px] font-semibold text-[#202223] leading-tight">
                    Preview Your Portal
                  </h1>
                </div>
                <p className="text-[#6d7175] text-sm max-w-2xl leading-relaxed">
                  Review how your customized portal will look to customers. Make
                  sure to save your design in the &quot;Customize Portal&quot;
                  tab before previewing.
                </p>
              </div>

              <div className="bg-white rounded-lg border border-[#e1e3e5] p-6 min-h-[600px]">
                <Preview form={form} showNotification={showNotification} />
              </div>
            </div>
          )}
          {tabs[2].name === tab && (
            <CodeScript form={form} showNotification={showNotification} />
          )}
        </div>
      </div>
    );
  },
);

export default EmbedClaimPortal;
